/* eslint-disable no-use-before-define */
import styled, { css } from 'styled-components';
import { zIndex } from 'styles';
import { animated } from 'react-spring';
import TOKENS from '@nokia-csf-uxr/freeform-design-tokens';
import alpha from 'styles/utils/alpha';
import { BORDER_BOX } from 'common';

const { SPACING, RADIUS } = TOKENS;

const EXPANDED_CONTROL_HEIGHT = 40;
const MIN_CARD_HEIGHT = '12.5rem'; // 200px
const CUBIC_BEZIER = 'cubic-bezier(0.77, 0, 0.175, 1)';
const TRANSITION_TOP = `top 0.4s ${CUBIC_BEZIER}`;
const TRANSITION_LEFT = `left 0.4s ${CUBIC_BEZIER}`;
const TRANSITION_BOX_SHADOW = 'box-shadow 0.1s ease-in 0.1s';
const TRANSITION_OPACITY = `opacity .4s ${CUBIC_BEZIER}`;

const SELECTED_STYLES = css`
  box-shadow: 0 0 0 ${SPACING.SPACING_2} ${props => props.$theme.selected.border};
`;
const SELECTED_W_FOCUS = css`
  box-shadow: 0 0 ${SPACING.SPACING_8} 0 ${props => props.$theme.hover.shadowColorOne},
    0 ${SPACING.SPACING_8} ${SPACING.SPACING_8} 0 ${props => props.$theme.hover.shadowColorTwo},
    0 0 0 ${SPACING.SPACING_2} ${props => props.$theme.selected.border}; // ELEVATION 8 + SELECTED + FOCUS
`;

const FOCUSED_STYLES = css`
  border-radius: ${RADIUS.RADIUS_2};
  box-sizing: border-box;
  box-shadow: 0 0 ${SPACING.SPACING_2} 0 ${props => props.$theme.hover.shadowColorOne},
    0 ${SPACING.SPACING_2} ${SPACING.SPACING_2} 0 ${props => props.$theme.hover.shadowColorTwo},
    0 0 0 ${SPACING.SPACING_2} ${props => props.$theme.focus.border};

  // focus + selected
  ${({ selected }) =>
    selected &&
    css`
      ${SELECTED_W_FOCUS}
    `}

  &:hover {
    box-shadow: 0 0 ${SPACING.SPACING_8} 0 ${props => props.$theme.hover.shadowColorOne},
      0 ${SPACING.SPACING_8} ${SPACING.SPACING_8} 0 ${props => props.$theme.hover.shadowColorTwo},
      0 0 0 ${SPACING.SPACING_2} ${props => props.$theme.focus.border}; // ELEVATION 8 + FOCUS box-shadow

    // focus + selected + hover
    ${({ selected }) =>
    selected &&
      css`
        ${SELECTED_W_FOCUS}
      `}
  }
  ${({ $disableAnimation }) => !$disableAnimation &&
  css`
    transition: ${TRANSITION_BOX_SHADOW};
  `}
`;

const SELECTABLE_STYLES = css`
  ${({ onSelect, selected }) =>
    (onSelect || selected) &&
    css`
      ${CardSelectionButton} {
        opacity: 1;
        ${({ $disableAnimation }) => !$disableAnimation &&
        css`
          transition: ${TRANSITION_OPACITY};
        `}
      }
    `}
`;

const HOVER_STYLES = css`
  box-shadow: 0 ${SPACING.SPACING_8} ${SPACING.SPACING_8} 0 ${props => props.$theme.hover.shadowColorOne},
    0 0 ${SPACING.SPACING_8} 0 ${props => props.$theme.hover.shadowColorTwo}; // elevation 8
  background-color: ${props => props.$theme.hover.lightBackgroundColor};
  // hover + selected (wihin Card Collection)
  ${SELECTABLE_STYLES}

  // hover + selected (outside of Card Collection)
  ${({ selected }) =>
    selected &&
    css`
      ${SELECTED_STYLES}
    `}
    ${({ $disableAnimation }) => !$disableAnimation &&
    css`
      transition: ${TRANSITION_BOX_SHADOW};
    `}
`;

const Card = styled.div`
  ${BORDER_BOX}
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const CardContainer = styled.div`
  ${BORDER_BOX}
  display: flex;
  flex-direction: column;
  overflow: visible;
  background-color: ${props => props.$theme.lightBackgroundColor};
  border-radius: ${RADIUS.RADIUS_2};
  box-shadow: 0 ${SPACING.SPACING_2} ${SPACING.SPACING_2} 0 ${props => props.$theme.shadowColorOne},
  0 0 ${SPACING.SPACING_2} 0 ${props => props.$theme.shadowColorTwo};// elevation 2
  box-sizing: border-box;
  position: relative;
  ${({ $disableAnimation }) => !$disableAnimation &&
  css`
    transition: ${TRANSITION_BOX_SHADOW}, ${TRANSITION_TOP}, ${TRANSITION_LEFT};
  `}
  &:focus {
    outline: none;
  }

  // HOVER
  ${({ hoverEffect }) =>
    hoverEffect &&
    css`
      &:hover {
        ${HOVER_STYLES}
      }
    `}

  // FOCUS
  ${({ focusEffect }) =>
    focusEffect &&
    css`
      &:focus:not(.react-draggable-dragging) {
        outline: none;
        ${FOCUSED_STYLES}
      }
    `}

  //MODAL 
  ${props =>
    props.$modal &&
    css`
      cursor: pointer;
    `}

  // EXPANDED
  ${({ expanded }) =>
    expanded &&
    css`
      z-index: ${zIndex.CARD.expanded};
    `}
  
  // SELECTED
  ${({ selected }) =>
    selected &&
    css`
      ${SELECTED_STYLES}
    `}

  // from CardCollection context, shows all selectable cards
  ${({ $showSelectableCards }) =>
    $showSelectableCards &&
    css`
      ${SELECTABLE_STYLES};
    `}
    ${({ $disableAnimation }) => !$disableAnimation &&
    css`
      transition: ${TRANSITION_BOX_SHADOW};
    `}
`;
const CardStackOne = styled.div`
  ${BORDER_BOX}  
  position: absolute;
  height: 0.1875rem;
  width: 98%;
  bottom: -0.1875rem;
  left: 50%;
  transform: translate(-50%, 0);
  background-color: ${props => props.$theme.lightBackgroundColor};
  border-radius: ${RADIUS.RADIUS_2};
  box-shadow: 0 ${SPACING.SPACING_2} ${SPACING.SPACING_2} 0 ${props => props.$theme.shadowColorOne},
    0 0 ${SPACING.SPACING_2} 0 ${props => props.$theme.shadowColorTwo}; // elevation 2
  z-index: -1; // Relative to CardContainer
`;
const CardStackTwo = styled(CardStackOne)`
  ${BORDER_BOX}  
  width: 96%;
  bottom: -0.375rem;
  z-index: -2; // Relative to CardContainer
`;

const CardSelectionButton = styled.div`
  ${BORDER_BOX}
  position: absolute;
  top: 0;
  left: 0;
  height: auto;
  width: 100%;
  z-index: 1;
  display: flex;
  opacity: 0;
  padding: ${SPACING.SPACING_4};
  ${({ focused, selected }) =>
    (focused || selected) &&
    css`
      opacity: 1;
    `};
  ${({ rtl }) =>
    rtl &&
    css`
      direction: rtl;
    `}
`;

const CardExpansionButton = styled(animated.div)`
  display: flex;
  margin-right: 0.125rem;
`;

const CardContentContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  ${({ $cardCollection }) =>
    $cardCollection &&
    css`
      height: auto;
      min-height: ${MIN_CARD_HEIGHT};
      ${({ $modal }) =>
    $modal &&
        css`
          height: 100%;
        `}
    `}
  ${({ rtl }) =>
    rtl &&
    css`
      direction: rtl;
    `}
`;

const CardFooter = styled.div`
  display: flex;
  ${({ expandable }) =>
    expandable &&
    css`
      min-height: ${EXPANDED_CONTROL_HEIGHT}px;
      align-items: center;
      justify-content: flex-end;
      width: 100%;
    `}
`;

const CardExpandedContent = styled(animated.div)`
  display: flex;
  overflow: hidden;
  ${({ $cardCollection }) =>
    $cardCollection &&
    css`
      overflow-y: hidden; /** in Cards, there appears to be a bit of overflow from scrollbar, this hides it */
    `}
  ${({ $expanded }) =>
    $expanded &&
    css`
      overflow-y: auto;
    `}
`;

const CardExpandedContentInner = styled.div``;

const ModalCard = styled.div`
  ${BORDER_BOX}
  position: fixed;
  top: 0;
  width: 100%;
  height: 100%;
  padding: 0;
  background-color: ${alpha(TOKENS.COLORS.BLACK, TOKENS.OPACITY.OPACITY_54)};
  z-index: ${zIndex.CARD.modal};
  ${({ $active }) => ($active ? 'display: block' : 'display: none ')}
`;

const ModalCardContent = styled(CardContainer)`
  ${({ $rtl }) =>
    $rtl &&
    css`
      direction: rtl;
    `}
  ${({ $active }) =>
    $active &&
    css`
      z-index: ${zIndex.CARD.modal};
    `};
`;

const CardOuterContainer = styled.div``;

const styles = {
  CardContainer,
  CardContentContainer,
  Card,
  CardStackOne,
  CardStackTwo,
  CardFooter,
  CardExpansionButton,
  CardSelectionButton,
  CardExpandedContent,
  CardExpandedContentInner,
  ModalCard,
  ModalCardContent,
  CardOuterContainer
};

export { styles as default };
